import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { User, MapPin, Briefcase } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  let currentUser = null;
  try {
    const auth = useAuth();
    currentUser = auth?.user;
  } catch (error) {
    console.log('Auth not available, public view');
  }
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUser();
    fetchUserPosts();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/public`);
      setUser(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPosts = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/posts`);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center">Loading...</div>;
  if (!user) return <div className="min-h-screen bg-background flex items-center justify-center">User not found</div>;

  const isOwnProfile = currentUser && currentUser.id === userId;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">← Back</Button>
        
        <Card>
          <CardContent className="p-8">
            <div className="flex items-start gap-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-4xl font-bold">
                {user.avatar_url ? <img src={user.avatar_url} onError={(e) => e.target.style.display='none'} className="w-full h-full rounded-full object-cover" alt={user.full_name} /> : user.full_name[0]}
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold mb-2">{user.full_name}</h1>
                <div className="flex items-center gap-3 mb-4">
                  <Badge>{user.role}</Badge>
                </div>
                <p className="text-muted-foreground">Member since {new Date(user.created_at).toLocaleDateString()}</p>
                {isOwnProfile && (
                  <Button onClick={() => navigate('/me/posts')} className="mt-4">Manage Posts</Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <h2 className="text-2xl font-bold my-6">Posts</h2>
        <div className="space-y-4">
          {posts.length === 0 ? (
            <p className="text-center text-muted-foreground py-10">No posts yet</p>
          ) : (
            posts.map(post => (
              <Card key={post.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline">{post.post_type}</Badge>
                    <span className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleString()}</span>
                  </div>
                  <p className="whitespace-pre-wrap mb-3">{post.text}</p>
                  {post.media_images && post.media_images.length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                      {post.media_images.map((img, idx) => (
                        <img key={idx} src={img} className="w-full h-32 object-cover rounded" alt="" />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
